import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { QuickPublish } from "@/components/QuickPublish";
import {
  CheckCircle2, Clock, AlertCircle, RotateCcw, Loader2, RefreshCw, Send, Inbox,
} from "lucide-react";

const STATUS = {
  PUBLISHED: { label: "Published", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  QUEUED: { label: "Queued", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30", icon: Clock },
  FAILED: { label: "Action required", cls: "bg-rose-500/15 text-rose-400 border-rose-500/30", icon: AlertCircle },
  EXPIRED_MAX_RECYCLES: { label: "Recycles done", cls: "bg-secondary text-muted-foreground border-border", icon: Clock },
  DRAFT: { label: "Draft", cls: "bg-secondary text-muted-foreground border-border", icon: Clock },
};

const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function Activity() {
  const { active } = useWorkspace();
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);
  const [retrying, setRetrying] = useState("");

  const load = useCallback(async () => {
    if (!active) return;
    try {
      const r = await api.get(`/posts?workspace_id=${active.id}`);
      const items = [...r.data].sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
      setPosts(items);
    } catch (e) { toast.error("Could not load activity."); }
  }, [active]);

  useEffect(() => { setPosts(null); load(); }, [load]);

  const retry = async (id) => {
    setRetrying(id);
    try {
      await api.post(`/posts/${id}/retry`);
      toast.success("Re-queued — it'll retry shortly.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed.");
    } finally { setRetrying(""); }
  };

  if (!active) return <div className="text-muted-foreground" data-testid="activity-no-workspace">Select a workspace to see activity.</div>;

  const counts = (posts || []).reduce((a, p) => { a[p.status] = (a[p.status] || 0) + 1; return a; }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Activity</h1>
          <p className="text-muted-foreground mt-1">Every published, queued and failed post — with one-click retry.</p>
        </div>
        <Button variant="outline" onClick={load} className="border-border" data-testid="activity-refresh">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {["PUBLISHED", "QUEUED", "FAILED"].map((s) => (
          <span key={s} className={`text-xs px-3 py-1 rounded-full border ${STATUS[s].cls}`} data-testid={`activity-count-${s.toLowerCase()}`}>
            {STATUS[s].label}: {counts[s] || 0}
          </span>
        ))}
      </div>

      {posts === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : posts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center" data-testid="activity-empty">
          <Inbox className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-4">No posts yet. Compose your first campaign to get started.</p>
          <Button onClick={() => navigate("/composer")} className="bg-primary hover:bg-primary/90 text-white" data-testid="activity-go-composer">
            <Send className="h-4 w-4 mr-2" /> Open Composer
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden divide-y divide-border" data-testid="activity-list">
          {posts.map((p) => {
            const st = STATUS[p.status] || STATUS.DRAFT;
            const StIcon = st.icon;
            return (
              <div key={p.id} className="flex items-start gap-4 p-4 bg-card" data-testid={`activity-row-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge className={`${st.cls} border text-[10px]`}><StIcon className="h-3 w-3 mr-1" />{st.label}</Badge>
                    <span className="text-xs capitalize text-muted-foreground">{p.platform}</span>
                    <span className="text-xs text-muted-foreground">· {fmt(p.scheduled_at)}</span>
                  </div>
                  <p className="text-sm text-foreground/90 line-clamp-2">{p.post_text}</p>
                  {p.status === "FAILED" && p.error && (
                    <p className="text-xs text-rose-400 mt-1" data-testid={`activity-error-${p.id}`}>{p.error}</p>
                  )}
                  <QuickPublish platform={p.platform} text={p.post_text} />
                </div>
                {(p.status === "FAILED" || p.status === "EXPIRED_MAX_RECYCLES") && (
                  <Button size="sm" variant="outline" onClick={() => retry(p.id)} disabled={retrying === p.id}
                    className="border-primary/40 text-primary shrink-0" data-testid={`activity-retry-${p.id}`}>
                    {retrying === p.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                    Retry
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
